import React from 'react'
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useHelpArticle, useHelpArticles } from '@api/helpCenter'
import NextLink from '@imports/NextLink'
import Breadcrumbs from '@components/Breadcrumbs'
import { toast } from 'react-toastify'
import { ContentCopy } from '@mui/icons-material'
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share'
import quillStyleObject from '@utils/quillStyleObject'
import ArticleUseful from './ArticleUseful'

function Article() {
  const { t } = useTranslation(['common', 'help-center'])
  const {
    query: { slug },
    locale,
  } = useRouter()
  const articleQuery = useHelpArticle(slug, { locale })
  const article = articleQuery.data

  const articlesQuery = useHelpArticles({ locale, category: article?.category?.id })
  const articlesOnThisPage = articlesQuery.data?.data ?? []

  const shareURL = `https://www.blueassembly.com/${locale}/help-center/${slug}`
  return (
    <Box py={10}>
      <Container maxWidth="xl">
        <Grid
          container
          columnSpacing={6}
          rowSpacing={3}
        >
          <Grid
            item
            xs={12}
            md={8}
          >
            <Breadcrumbs
              items={[
                { text: t('help-center:home'), url: '/' },
                { text: t('help-center:help_center'), url: '/help-center' },
                ...(article?.category?.title
                  ? [
                      {
                        text: article?.category?.title ?? '-',
                        url: `/help-center?category=${article?.category?.id}`,
                      },
                    ]
                  : []),
              ]}
            />
            <Box
              pb={2}
              sx={{ overflow: 'hidden' }}
            >
              {!!articleQuery.isLoading && (
                <Grid
                  container
                  justifyContent="center"
                >
                  <CircularProgress />
                </Grid>
              )}
              {!articleQuery.isLoading && !article && (
                <Typography
                  variant="h6"
                  textTransform="capitalize"
                  textAlign="center"
                  display="block"
                  pt={2}
                >
                  {t('help-center:article_not_found')}
                </Typography>
              )}
              {!articleQuery.isLoading && article && (
                <Grid
                  container
                  spacing={3}
                >
                  <Grid
                    item
                    xs={12}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        display="block"
                        variant="h4"
                        fontWeight={700}
                      >
                        {article.title}
                      </Typography>
                      <Box>
                        <Grid
                          container
                          spacing={0.5}
                        >
                          <Grid
                            item
                            sx={{ lineHeight: 0 }}
                          >
                            <IconButton
                              size="small"
                              sx={{ backgroundColor: '#DDD', p: '3px' }}
                              aria-label="copy to clipboard"
                              onClick={() => {
                                navigator.clipboard
                                  .writeText(shareURL)
                                  .then(() => {
                                    toast.success(t('common:Share_link_copied_to_clipboard'))
                                  })
                                  .catch(() => {
                                    toast.error(t('common:Failed_to_copy_share_link'))
                                  })
                              }}
                            >
                              <ContentCopy
                                sx={{ color: '#000', p: '1px' }}
                                fontSize="inherit"
                              />
                            </IconButton>
                          </Grid>
                          <Grid item>
                            <FacebookShareButton url={shareURL}>
                              <FacebookIcon
                                size={24}
                                round
                              />
                            </FacebookShareButton>
                          </Grid>
                          <Grid item>
                            <TwitterShareButton url={shareURL}>
                              <TwitterIcon
                                size={24}
                                round
                              />
                            </TwitterShareButton>
                          </Grid>
                          <Grid item>
                            <LinkedinShareButton url={shareURL}>
                              <LinkedinIcon
                                size={24}
                                round
                              />
                            </LinkedinShareButton>
                          </Grid>
                        </Grid>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <Box
                      dangerouslySetInnerHTML={{ __html: article?.html }}
                      sx={quillStyleObject}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                  >
                    <ArticleUseful id={article.id} />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Grid
              container
              spacing={3}
            >
              <Grid
                item
                sx={{ display: { xs: 'none', md: 'block' } }}
              >
                <Divider
                  sx={{ minHeight: 350 }}
                  orientation="vertical"
                />
              </Grid>
              <Grid
                item
                xs
              >
                <Typography
                  variant="body1"
                  display="block"
                  fontWeight={700}
                  textTransform="capitalize"
                  sx={{ py: 3.5 }}
                >
                  {t('help-center:related_articles')}
                </Typography>
                <Box pl={2}>
                  <Grid
                    container
                    spacing={1.5}
                  >
                    {!!articlesQuery.isLoading && (
                      <Grid
                        container
                        justifyContent="center"
                      >
                        <CircularProgress />
                      </Grid>
                    )}
                    {articlesOnThisPage.map((articleOnThisPage) => (
                      <Grid
                        item
                        xs={12}
                        key={articleOnThisPage.id}
                      >
                        <Typography
                          variant="body2"
                          display="block"
                          fontWeight={500}
                          color={
                            articleOnThisPage.id === article?.id ? 'secondary.main' : 'primary.main'
                          }
                        >
                          <NextLink href={`/help-center/${articleOnThisPage.slug}`}>
                            - {articleOnThisPage.title}
                          </NextLink>
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default Article
